import React, { useState } from 'react'
import {
    useTheme,
    Typography,
    TextField,
    Button,
    Paper,
    Box,
    CircularProgress,
    IconButton,
    Popover,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import SaveIcon from '@material-ui/icons/Save'
import { Container } from '../../../components'
import { useOutreach } from '../../../hooks/Outreach/useOutreach'

type EnrichmentFieldType =
    | 'personal_email'
    | 'alternate_personal_emails'
    | 'phone_number'
    | 'linkedin_profile'

interface FieldConfig {
    field: EnrichmentFieldType
    label: string
    helperText: string
}

const FIELD_CONFIGS: FieldConfig[] = [
    {
        field: 'personal_email',
        label: 'Personal Email',
        helperText: 'Token cost for enriching personal email addresses',
    },
    {
        field: 'alternate_personal_emails',
        label: 'Alternate Personal Emails',
        helperText:
            'Token cost for enriching alternate personal email addresses',
    },
    {
        field: 'phone_number',
        label: 'Phone Number',
        helperText: 'Token cost for enriching phone numbers',
    },
    {
        field: 'linkedin_profile',
        label: 'LinkedIn Profile',
        helperText: 'Token cost for enriching LinkedIn profiles',
    },
]

export const TokensTab: React.FC = () => {
    const theme = useTheme()
    const {
        enrichmentTokenCosts,
        isLoadingEnrichmentTokenCosts,
        updateEnrichmentTokenCosts,
    } = useOutreach({ loadTokenCosts: true })

    const [anchorEl, setAnchorEl] = useState<{
        element: HTMLElement
        field: EnrichmentFieldType
    } | null>(null)
    const [editValue, setEditValue] = useState<string>('')

    const getValue = (field: EnrichmentFieldType): number => {
        const cost = enrichmentTokenCosts?.find(
            (item) => item.output_field === field,
        )
        return cost?.token_cost || 0
    }

    const handleEditClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        field: EnrichmentFieldType,
    ) => {
        const currentValue = getValue(field)
        setEditValue(currentValue.toString())
        setAnchorEl({ element: event.currentTarget, field })
    }

    const handlePopoverClose = () => {
        setAnchorEl(null)
        setEditValue('')
    }

    const handleSave = async () => {
        if (!anchorEl) return

        const numValue = parseInt(editValue, 10)
        if (isNaN(numValue) || numValue < 0) {
            return
        }

        try {
            await updateEnrichmentTokenCosts.mutateAsync({
                output_field: anchorEl.field,
                token_cost: numValue,
            })
            handlePopoverClose()
        } catch (error) {
            console.error('Error saving token cost:', error)
        }
    }

    if (isLoadingEnrichmentTokenCosts) {
        return (
            <Container
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.grey[200],
                }}
            >
                <CircularProgress />
            </Container>
        )
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
                backgroundColor: theme.palette.grey[200],
                padding: theme.spacing(3),
            }}
        >
            <Paper
                elevation={2}
                style={{
                    padding: theme.spacing(3),
                    maxWidth: 800,
                    margin: '0 auto',
                }}
            >
                <Typography variant="h5" gutterBottom>
                    Enrichment Token Costs
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginBottom: theme.spacing(3) }}
                >
                    Configure the token cost for each enrichment type. These
                    costs apply globally to all properties.
                </Typography>

                <Box
                    display="flex"
                    flexDirection="column"
                    style={{ gap: theme.spacing(4) }}
                >
                    {FIELD_CONFIGS.map((config) => (
                        <Box
                            key={config.field}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box flex={1}>
                                <Typography
                                    variant="h6"
                                    style={{
                                        fontWeight:
                                            theme.typography.fontWeightBold,
                                        marginBottom: theme.spacing(1),
                                    }}
                                >
                                    {config.label}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="textSecondary"
                                >
                                    {getValue(config.field)} tokens
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={(e) =>
                                    handleEditClick(e, config.field)
                                }
                                color="primary"
                            >
                                <EditIcon />
                            </IconButton>
                        </Box>
                    ))}
                </Box>

                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl?.element}
                    onClose={handlePopoverClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <Box
                        style={{
                            padding: theme.spacing(2),
                            minWidth: 300,
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Edit Token Cost
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            label="Token Cost"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            variant="outlined"
                            margin="normal"
                            inputProps={{ min: 0, step: 1 }}
                            helperText={
                                anchorEl
                                    ? FIELD_CONFIGS.find(
                                          (c) => c.field === anchorEl.field,
                                      )?.helperText
                                    : ''
                            }
                        />
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            style={{ marginTop: theme.spacing(2) }}
                        >
                            <Button
                                onClick={handlePopoverClose}
                                style={{ marginRight: theme.spacing(1) }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                disabled={updateEnrichmentTokenCosts.isLoading}
                            >
                                {updateEnrichmentTokenCosts.isLoading
                                    ? 'Saving...'
                                    : 'Save'}
                            </Button>
                        </Box>
                    </Box>
                </Popover>
            </Paper>
        </Container>
    )
}
